import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { Box, Grid,  Typography, Skeleton } from '@mui/material';
import { useSnackbar } from 'notistack';
import useFollowers from '../../utils/useFollowers';
import FollowerList from '../FollowerList/FollowerList';
import { ENV } from '../../constants';
import Header from '../Header';
import EditButton from '../Controls/EditButton';
import UserAvatar from '../GroupView/UserAvatar';

const styles = {
  user: {
    marginTop: '25px',
    marginBottom: '39px',
  },
  userHandle: {
    letterSpacing: 'initial',
    textTransform: 'initial',
    height: '30px',
  },
  userName: {
    fontWeight: '400',
    fontSize: '16px',
    lineHeight: '20px',
  },
  userAvatar: {
    height: '46px',
    width: '46px',
    marginRight: '15px',
  },
  editButton: {
    margin: '-6px -8px',
  },
}

export default function UserViewDetails({ user, onUserChange }) {
  const { _id: id } = user;
  const [defaultReply, setDefaultReply] = useState(!!user.defaultAllowReply);
  const location = useLocation();
  const [
    { loading, followers, unregisteredFollowers },
    setFollowers,
  ] = useFollowers(id, 'user');
  
  const { enqueueSnackbar } = useSnackbar();

  const post = async (endpoint, data) => {
    const response = await fetch(`${ENV.API_URL}/user/${endpoint}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    try {
      const result = await response.json();
      return result;
    } catch {
      return false;
    }
  };

  const postAllowReply = async (userId, followerId, allowReply) => {
    return post('setAllowReply', { userId, followerId, allowReply });
  };

  const postDefaultAllowReply = async (userId, allowReply) => {
    return post('setDefaultAllowReply', { userId, allowReply });
  };

  const [saving, setSaving] = useState(false);
  const onAllowReplyToggleClick = async (userId, allowReply) => {
    if (saving) return;

    setSaving(true);
    // Toggle user's local value for intant feedback
    setFollowers((prevFollowers) =>
      prevFollowers.map((f) => (f._id === userId ? { ...f, allowReply } : f))
    );

    const result = await postAllowReply(id, userId, allowReply);

    if (!result.success) {
      // undo change and display error message
      setFollowers((prevFollowers) =>
        prevFollowers.map((f) =>
          f._id === userId ? { ...f, allowReply: !allowReply } : f
        )
      );

      enqueueSnackbar('Failed to save change', { variant: 'error' });
    } else {
      const feedback = allowReply ? 'enabled' : 'disabled';

      enqueueSnackbar(`User reply ${feedback}`, { variant: 'success' });
    }

    setSaving(false);
  };

  const [savingDefault, setSavingDefault] = useState(false);
  const onDefaultAllowReplyToggleClick = async (allowReply) => {
    if (savingDefault) return;

    setSavingDefault(true);
    // Toggle local value for intant feedback
    setDefaultReply(allowReply);

    const result = await postDefaultAllowReply(id, allowReply);

    if (!result.success) {
      // undo change and display error message
      setDefaultReply(!allowReply);

      enqueueSnackbar('Failed to save change', { variant: 'error' });
    } else {
      enqueueSnackbar(`Settings updated`, { variant: 'success' });
      onUserChange({ ...user, defaultAllowReply: allowReply });
    }

    setSavingDefault(false);
  };

  return (
    <Box>
      <Header
        back
        title={
          <Grid container alignItems={"center"}>
            <Grid item>
              <UserAvatar sx={styles.userAvatar} user={user} />
            </Grid>
            <Grid item>
              <Typography
                color="primary"
                variant="subtitle1"
                sx={styles.userHandle}
              >
                {user.displayHandle ? `@${user.displayHandle}` : ' '}
              </Typography>
              <Typography
                color="primary"
                variant="body1"
                sx={styles.userName}
              >
                {user.firstName} {user.lastName}
              </Typography>
            </Grid>
          </Grid>
        }
        action={
          <EditButton
            onClick={(navigate) =>
              navigate(`${location.pathname}/edit`)
            }
          />
        }
      />
      <Box>
        {loading ? (
          <Skeleton />
        ) : (
          <FollowerList
            defaultSettingTip="Allow new followers to reply directly to you by using your @username."
            defaultAllowReply={defaultReply}
            followers={followers}
            unregisteredFollowers={unregisteredFollowers}
            onDefaultAllowReplyChange={onDefaultAllowReplyToggleClick}
            savingDefault={savingDefault}
            onFollowerAllowReplyChange={onAllowReplyToggleClick}
            savingFollower={saving}
          />
        )}
      </Box>
    </Box>
  );
}

UserViewDetails.propTypes = {
  user: PropTypes.shape({
    _id: PropTypes.string,
    defaultAllowReply: PropTypes.bool,
    displayHandle: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    photo: PropTypes.string,
  }),
  onUserChange: PropTypes.func,
};
